import { ref, readonly } from "vue";
import { KnockGuideClient } from "@knocklabs/client";
import configService from "@/services/config";

/**
 * Global Knock Guides wiring (ER-11934, ER-12090).
 *
 * The Feed half of the Knock integration lives in App.vue's mounted(); this
 * composable is the guide counterpart. It is a module-level singleton: every
 * caller shares ONE KnockGuideClient so that:
 *
 *   - exactly one client proxies history.pushState / replaceState (per-page
 *     clients used to clobber the global client's route tracking on cleanup);
 *   - banner pages (Organization, As a Coach, As Employee, As an Architect,
 *     Your Stuff) read from the same store instead of constructing their own.
 *
 * The client is authenticated through the same getKnock() singleton App.vue
 * already uses for feeds. URL-based targeting is evaluated by the SDK itself
 * (trackLocationFromWindow: true), so no per-route eligibility code lives here.
 *
 * The returned refs are read-only to consumers; only the client store
 * subscription mutates them.
 */

// ---------------------------------------------------------------------------
// Module-level singleton state (shared by every useKnockGuides() caller).
// ---------------------------------------------------------------------------
let guideClient = null;
let unsubscribeStore = null;

const activeGuide = ref(null);
const activeStep = ref(null);

// Banner pages register a re-select callback so they react to store updates
// (fetch, realtime websocket events, location changes, engagement mutations).
const bannerListeners = new Set();

const notifyBannerListeners = () => {
  bannerListeners.forEach((listener) => {
    try {
      listener();
    } catch (error) {
      console.warn("[Knock] Banner guide listener failed:", error);
    }
  });
};

const resolveEligibleModalGuide = (state) => {
  const guide = guideClient.selectGuide(state, { type: "modal" });
  if (!guide) return null;

  // getStep() returns the next unarchived step (or undefined when every step
  // is archived), so an archived/dismissed/interacted guide stops being
  // selected here and the modal never reappears.
  const step = guide.getStep();
  if (!step) return null;

  return { guide, step };
};

const refreshFromStore = (state) => {
  const resolved = resolveEligibleModalGuide(state);
  activeGuide.value = resolved ? resolved.guide : null;
  activeStep.value = resolved ? resolved.step : null;

  // Banner pages re-select their guide by key on every store update.
  notifyBannerListeners();
};

/**
 * Select a guide from the singleton's store.
 *
 * - `{ key }` (banner pages): find the guide by its Knock key using the same
 *   non-staging selectGuides() lookup the pages previously did in-place.
 * - `{ type }` (modal): delegate to the SDK's staged single-guide selector.
 */
const selectGuide = ({ type, key } = {}) => {
  if (!guideClient) return null;
  const state = guideClient.store.state;

  if (key) {
    const filters = type ? { type, key } : { key };
    const guides = guideClient.selectGuides(state, filters) || [];
    return guides.find((guide) => guide.key === key) ?? null;
  }

  return guideClient.selectGuide(state, type ? { type } : {}) ?? null;
};

/**
 * Register a banner re-select callback. Fires on every store update, and
 * immediately when the client is already initialized so a late-mounting page
 * does not miss guides that were fetched before it appeared.
 *
 * Returns an unsubscribe function.
 */
const subscribeToGuides = (listener) => {
  bannerListeners.add(listener);
  if (guideClient) {
    try {
      listener();
    } catch (error) {
      console.warn("[Knock] Banner guide listener failed:", error);
    }
  }
  return () => {
    bannerListeners.delete(listener);
  };
};

const initialize = (knock) => {
  if (!knock) {
    console.warn("[Knock] Client not initialized - missing API key");
    return false;
  }

  // Idempotent: App.vue may re-run mounted() after a logout/login cycle, and
  // the singleton must never construct a second client.
  if (guideClient) {
    return true;
  }

  const channelId = configService.get("knockGuideChannelId");
  if (!channelId) {
    console.warn("[Knock] No guide channel ID configured, skipping guide init");
    return false;
  }

  guideClient = new KnockGuideClient(knock, channelId, {}, { trackLocationFromWindow: true });

  unsubscribeStore = guideClient.store.subscribe(() => {
    refreshFromStore(guideClient.store.state);
  });

  // Realtime websocket updates + the initial guide fetch. Fetch is fire-and-
  // forget here; the store subscription above is what flips the active step.
  guideClient.subscribe();
  guideClient.fetch().catch((error) => {
    console.warn("[Knock] Failed to fetch guides:", error);
  });

  return true;
};

const clear = () => {
  activeGuide.value = null;
  activeStep.value = null;
};

const cleanup = () => {
  if (typeof unsubscribeStore === "function") {
    unsubscribeStore();
    unsubscribeStore = null;
  }
  if (guideClient) {
    guideClient.cleanup();
    guideClient = null;
  }
  activeGuide.value = null;
  activeStep.value = null;
  bannerListeners.clear();
};

export function useKnockGuides() {
  return {
    activeGuide: readonly(activeGuide),
    activeStep: readonly(activeStep),
    selectGuide,
    subscribeToGuides,
    initialize,
    clear,
    cleanup,
  };
}
